<script lang="ts">
	/**
	 * A Flowbite `Button` that navigates through the router.
	 *
	 * Passing `href` to `Button` makes it render a real `<a>` — which is what we
	 * want for accessibility and middle-click / "open in new tab" — but a plain
	 * anchor click would reload the whole SPA. So the click is intercepted and
	 * turned into a `push()`.
	 *
	 * ```svelte
	 * <RoutedButton href="/Users" color="primary" size="sm">
	 *   <i class="fa-solid fa-users mr-2"></i>View users
	 * </RoutedButton>
	 * ```
	 */

	import { Button } from 'flowbite-svelte';
	import type { ButtonProps } from 'flowbite-svelte';
	import { push } from '../abstract-spa-router';

	interface Props {
		/** Destination path. Omit for a plain button that only fires `onclick`. */
		href?: string | undefined;
		/** Anchor target. Any value other than `_self` opts out of routing. */
		target?: string | undefined;
		/** Whether the button has pill-shaped (fully rounded) corners. */
		pill?: boolean | undefined;
		/** Whether to render the button outlined instead of filled. */
		outline?: boolean | undefined;
		/** Size of the button — controls padding and font size. */
		size?: 'xs' | 'sm' | 'lg' | 'xl' | 'md' | undefined;
		/** HTML button type attribute for form submission behaviour. */
		type?: 'button' | 'submit' | 'reset' | undefined;
		/** Colour variant controlling the button's visual appearance. */
		color?: ButtonProps['color'];
		/** Whether to apply a drop-shadow effect to the button. */
		shadow?: boolean | undefined;
		/** Blocks navigation and dims the button. */
		disabled?: boolean | undefined;
		/** Extra classes for the button. */
		class?: string | undefined;
		/** Tooltip text. Required in practice for an icon-only button. */
		title?: string | undefined;
		/** Accessible name, for when the label is an icon rather than text. */
		'aria-label'?: string | undefined;
		/**
		 * Runs before navigating; return `false` to cancel it. This is the hook for
		 * "are you sure?" confirmations and unsaved-changes guards — the button
		 * stays a link, but the navigation is conditional.
		 */
		clickHandler?: () => boolean;
		/** Legacy alias for `clickHandler`. */
		fnClickHandler?: () => boolean;
		/** Forwarded click handler, fired after navigation is dispatched. */
		onclick?: (_e: MouseEvent) => void;
		children?: import('svelte').Snippet;
	}

	let {
		href = undefined,
		target = undefined,
		pill = undefined,
		outline = undefined,
		size = undefined,
		type = undefined,
		color = undefined,
		shadow = undefined,
		disabled = undefined,
		class: cssClass = undefined,
		title = undefined,
		'aria-label': ariaLabel = undefined,
		clickHandler = undefined,
		fnClickHandler = undefined,
		onclick = undefined,
		children
	}: Props = $props();

	/**
	 * Intercept the click and turn it into a router push.
	 *
	 * The router only understands in-app paths. External URLs, new-tab targets
	 * and hrefless buttons must keep the browser's native behaviour, and
	 * modifier-clicks (ctrl/cmd/shift/middle) must stay "open in a new tab" or
	 * the link feels broken.
	 */
	const fnNavigate = (event: MouseEvent) => {
		onclick?.(event);

		/* Only in-app paths can be routed; external and protocol-relative URLs
		   keep the browser's native navigation. */
		if (!href || !href.startsWith('/') || href.startsWith('//')) return;
		/* A new-tab target is a deliberate opt-out of routing. */
		if (target && target !== '_self') return;
		/* Modifier and middle clicks must stay "open in a new tab", or the link
		   feels broken. */
		if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0)
			return;

		event.preventDefault();

		/* Past this point the anchor's own navigation is cancelled, so a disabled
		   button simply does nothing. */
		if (disabled) return;
		/* The caller's guard gets the last word — a false return cancels. */
		if (!(clickHandler ?? fnClickHandler ?? (() => true))()) return;

		push(href);
	};
</script>

<Button
	class={cssClass}
	{href}
	{target}
	{size}
	{color}
	{type}
	{shadow}
	{pill}
	{outline}
	{disabled}
	{title}
	aria-label={ariaLabel}
	onclick={fnNavigate}
>
	{@render children?.()}
</Button>
